import React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from "@mui/icons-material/Close";
import { get_t_str_shortened_max } from '../../../../helpers/helpers';
import AdvancedForecastLineChart from './AdvanceForecastLineChart';

function AdvancedForecastingModal(props) {
  // Only show actuals when the day is fully complete (not today)
  const showActuals = props.actual_intraday_metrics && Object.keys(props.actual_intraday_metrics).length > 0 && !props.isToday;

  function adjustment_icon(value) {
    if (value?.toFixed(3) === "0.000") {
      return "";
    }
    if (value < -0.2) {
      return <SouthEastIcon fontSize="small" />;
    }
    if (value < 0) {
      return <TrendingDownIcon fontSize="small" />;
    }
    if (value > 0.2) {
      return <NorthEastIcon fontSize="small" />;
    }
    if (value > 0) {
      return <TrendingUpIcon fontSize="small" />;
    } else {
      return "";
    }
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format number to K format
  const formatToK = (num) => {
    if (num < 1000) return Math.round(num);
    const valueInK = (Math.round(num / 100) / 10).toFixed(1);
    return `${valueInK}k`;
  };

  // Calculate percentage change
  const getPercentChange = () => {
    if (props.initial_forecast === 0) return 0;
    const change = ((props.adjusted_forecast - props.initial_forecast) / props.initial_forecast) * 100;
    return Math.round(change);
  };

  // Calculate actual vs forecast variance
  const getActualForecastVariance = () => {
    if (!showActuals || props.adjusted_forecast === 0) return 0;

    // Assuming we need to calculate total actual sales
    const actualTotal = props.actual_sales

    const change = ((actualTotal - props.adjusted_forecast) / props.adjusted_forecast) * 100;
    return Math.round(change);
  };

  // Modal style
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "96%",
    height: "99%",
    maxHeight: "99%",
    bgcolor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    p: 0,
    overflow: "auto",
  };

  const positiveForecastedSales =
    props.daily_intraday_metrics?.["Sales"]?.filter(
      (i) => i.total_value > 0
    ) || [];
  const positiveActualSales = showActuals
    ? props.actual_intraday_metrics?.["Sales"]?.filter(
        (i) => i.total_value > 0
      ) || []
    : [];

  const allQhNums = [
    ...positiveForecastedSales.map((d) => d.qh_num),
    ...positiveActualSales.map((d) => d.qh_num),
  ];

  const fallbackQhNums = [
    ...(props.daily_intraday_metrics?.["Sales"] || []).map((d) => d.qh_num),
    ...(showActuals
      ? (props.actual_intraday_metrics?.["Sales"] || []).map((d) => d.qh_num)
      : []),
  ];
  const displayQhNums = allQhNums.length > 0 ? allQhNums : fallbackQhNums;
  const start_qhnum =
    displayQhNums.length > 0 ? Math.floor(Math.min(...displayQhNums)) : 0;
  const end_qhnum =
    displayQhNums.length > 0 ? Math.ceil(Math.max(...displayQhNums) + 0.5) : 24;

  const numOfHourBoxes = Math.max(end_qhnum - start_qhnum, 1);
  const widthOfEachHourBox = 100 / numOfHourBoxes;
  const widthOfEachHalfTick = widthOfEachHourBox / 2;

  // Filter metrics for the time range
  const filteredMetrics = {};
  ["Sales", "Transactions", "Items", "Forecasted Labor", "Forecasted Hours", "Scheduled Labor", "Scheduled Hours"].forEach(metric => {
    if (props.daily_intraday_metrics?.[metric]) {
      filteredMetrics[metric] = props.daily_intraday_metrics[metric].filter(
        i => i.qh_num >= start_qhnum && i.qh_num < end_qhnum
      );
    }
  });

  // Filter actual metrics for the time range
  const filteredActualMetrics = {};
  if (showActuals) {
    ["Sales", "Transactions", "Items"].forEach(metric => {
      if (props.actual_intraday_metrics?.[metric]) {
        filteredActualMetrics[metric] = props.actual_intraday_metrics[metric].filter(
          i => i.qh_num >= start_qhnum && i.qh_num < end_qhnum
        );
      }
    });
  }

  // Get color based on sales value
  const getSalesColor = (value, isActual = false) => {
    const maxSales = Math.max(
      ...(filteredMetrics["Sales"] || []).map(item => item.total_value),
      ...(filteredActualMetrics["Sales"] || []).map(item => item.total_value),
      1
    );
    const intensity = Math.min(Math.max((value / maxSales) * 100, 5), 100);

    if (isActual) {
      // Use teal color for actual sales (teal-600: #0d9488)
      return {
        backgroundColor: `rgba(13, 148, 136, ${intensity / 100})`,
        color: intensity > 50 ? 'white' : '#1f2937'
      };
    }

    // Use violet for forecasted sales
    return {
      backgroundColor: `rgba(139, 92, 246, ${intensity / 100})`,
      color: intensity > 50 ? 'white' : '#1f2937'
    };
  };

  const forecastedSalesArray = filteredMetrics?.["Sales"] || [];
  const actualSalesArray = filteredActualMetrics?.["Sales"] || [];
  const hasActualSalesData = actualSalesArray.length > 0;

  const extendSalesArrayForChart = (salesArray) => {
    if (!salesArray || salesArray.length === 0) return [];

    const lastQhNum = salesArray[salesArray.length - 1]?.qh_num;
    if (lastQhNum === undefined || lastQhNum === null) return salesArray;

    return [...salesArray, { qh_num: lastQhNum + 0.5, total_value: 0 }];
  };

  // Format data for line chart
  const dataForLineChart = extendSalesArrayForChart(forecastedSalesArray).map((item) => ({
    qh_num: item.qh_num,
    forecast_value: item.total_value ?? 0,
  }));

  // Add actual sales data if available
  let chartData = dataForLineChart;
  if (showActuals && hasActualSalesData) {
    // Extend the array with a final zero point for better visualization
    const extendedActual = extendSalesArrayForChart(actualSalesArray).map((item) => ({
      qh_num: item.qh_num,
      actual_value: item.total_value ?? 0,
    }));
    const chartQhNums = Array.from(new Set([
      ...dataForLineChart.map(item => item.qh_num),
      ...extendedActual.map(item => item.qh_num),
    ])).sort((a, b) => a - b);

    // Transform data for the chart component
    chartData = chartQhNums.map(qhNum => {
      const matchingForecast = dataForLineChart.find(item => item.qh_num === qhNum);
      const matchingActual = extendedActual.find(a => a.qh_num === qhNum);
      return {
        qh_num: qhNum,
        forecast_value: matchingForecast ? matchingForecast.forecast_value : 0,
        actual_value: matchingActual ? matchingActual.actual_value : null
      };
    });
  }

  // Calculate actual sales total
  const actualSalesTotal = showActuals
    ? props.actual_sales
    : 0;

  return (
    <Modal
      open={props.open}
      onClose={() => props.handleClose()}
      aria-labelledby="advanced-forecasting-modal"
    >
      <Box sx={style}>
        {/* Header Section */}
        <div className="bg-white px-8 py-5 text-slate-900 flex justify-between items-center">
          <h2 className="text-xl font-medium">Forecast Breakdown</h2>
          <button
            onClick={() => props.handleClose()}
            className="text-gray-500 hover:text-gray-600 p-1 rounded-full transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Info Bar */}
        <div className="bg-gray-50 px-8 py-4 border-y border-gray-200 flex flex-wrap gap-x-8 gap-y-2">
          <div className="flex items-center text-gray-700">
            <CalendarTodayIcon className="mr-2 text-violet-700" fontSize="small" />
            <span className="text-sm font-medium">{props.date_name}</span>
          </div>
          <div className="flex items-center text-gray-700">
            <AccessTimeIcon className="mr-2 text-violet-700" fontSize="small" />
            <span className="text-sm">
              {props.isClosed ? "Closed" : props.hooString}
            </span>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-8 overflow-y-auto" style={{maxHeight: "calc(90vh - 150px)"}}>
          {/* Forecast Cards */}
          <div className="flex items-center gap-6 mb-6 flex-wrap">
            <div className="bg-white border border-gray-100 rounded-lg shadow-sm px-5 py-3 flex-1 min-w-[200px]">
              <div className="text-gray-500 text-sm mb-1">Initial Forecast</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(props.initial_forecast)}
              </div>
            </div>

            {(props.initial_forecast !== props.adjusted_forecast) &&
              <div className="bg-white border border-gray-100 rounded-lg shadow-sm px-5 py-3 flex-1 min-w-[200px]">
                <div className="flex justify-between items-start">
                  <div className="text-gray-500 text-sm mb-1">Adjusted Forecast</div>
                  <div className={`flex items-center ${getPercentChange() > 0 ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"} text-sm font-medium px-2 py-0.5 rounded-full`}>
                    <div>
                      {adjustment_icon(props.icon_value)}
                    </div>
                    <span>{getPercentChange()}%</span>
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(props.adjusted_forecast)}
                </div>
              </div>
            }

            {showActuals && (
              <div className="bg-white border border-gray-100 rounded-lg shadow-sm px-5 py-3 flex-1 min-w-[200px]">
                <div className="flex justify-between items-start">
                  <div className="text-gray-500 text-sm mb-1">Actual Sales</div>
                  <div className={`flex items-center ${getActualForecastVariance() >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"} text-sm font-medium px-2 py-0.5 rounded-full`}>
                    <div>
                      {adjustment_icon(getActualForecastVariance() / 100)}
                    </div>
                    <span>{getActualForecastVariance()}%</span>
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(actualSalesTotal)}
                </div>
              </div>
            )}

          </div>

          {/* Forecast Table */}
          <div className="mb-3 font-medium text-gray-700">Half-Hourly Breakdown</div>

          <div className="overflow-x-auto">
            <div className="min-w-full rounded-lg border border-gray-200 overflow-hidden shadow-sm">
              {/* Time Header Row */}
              <div className="w-full bg-gray-50 border-b border-gray-200 flex">
                <div className="w-20 bg-gray-100 border-r border-gray-200 flex items-center justify-center py-2">
                  <span className="text-sm font-medium text-gray-500">Time</span>
                </div>
                <div className="flex-1 flex">
                  {Array.from({ length: numOfHourBoxes }).map((_, index) => (
                    <div
                      key={index}
                      className={`flex-1 flex flex-col ${index < numOfHourBoxes - 1 ? "border-r border-gray-200" : ""}`}
                    >
                      <div className="py-2 flex items-center justify-center">
                        <p className="text-sm font-medium text-gray-600">
                          {get_t_str_shortened_max(index + start_qhnum)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sales Data Row */}
              {filteredMetrics.hasOwnProperty("Sales") && (
                <div className="w-full flex bg-white">
                  <div className="w-20 bg-gray-50 border-r border-gray-200 flex items-center justify-center py-3">
                    <span className="text-sm font-medium text-gray-600">Forecast</span>
                  </div>
                  <div className="flex-1 flex">
                    {forecastedSalesArray?.map((value, hourIndex) => {
                      return (
                        <div
                          key={hourIndex}
                          className={`flex items-center justify-center py-3 ${
                            hourIndex < filteredMetrics["Sales"].length - 1 ? "border-r border-gray-100" : ""
                          }`}
                          style={{
                            width: `${widthOfEachHalfTick}%`,
                            ...getSalesColor(value.total_value)
                          }}
                        >
                          <p className="text-sm font-medium">
                            ${value.total_value < 1000
                              ? Math.round(value.total_value)
                              : formatToK(value.total_value)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actual Sales Data Row */}
              {showActuals && filteredActualMetrics.hasOwnProperty("Sales") && (
                <div className="w-full flex bg-white border-t border-gray-100">
                  <div className="w-20 bg-gray-50 border-r border-gray-200 flex items-center justify-center py-3">
                    <span className="text-sm font-medium text-gray-600">Actual</span>
                  </div>
                  <div className="flex-1 flex">
                    {actualSalesArray?.map((value, hourIndex) => {
                      return (
                        <div
                          key={hourIndex}
                          className={`flex items-center justify-center py-3 ${
                            hourIndex < actualSalesArray.length - 1 ? "border-r border-gray-100" : ""
                          }`}
                          style={{
                            width: `${widthOfEachHalfTick}%`,
                            ...getSalesColor(value.total_value, true)
                          }}
                        >
                          <p className="text-sm font-medium">
                            ${value.total_value < 1000
                              ? Math.round(value.total_value)
                              : formatToK(value.total_value)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Line Chart */}
        <div className="mb-8">
          <AdvancedForecastLineChart
            data={chartData}
            hasActualData={hasActualSalesData}
          />
        </div>
      </Box>
    </Modal>
  );
}

export default AdvancedForecastingModal;
